import api from './api';

class EnterpriseLogService {
  /**
   * Fetch enterprise logs with advanced filters and pagination
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} API response with logs data
   */
  static async getLogs(params = {}) {
    try {
      const response = await api.get('/enterprise-logs', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch enterprise logs:', error);
      if (error.response && error.response.status === 401) {
        return {
          status: 0,
          message: 'Authentication required to access logs',
          data: null
        };
      }
      throw error;
    }
  }

  /**
   * Get audit trail logs with enhanced filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} API response with audit trail data
   */
  static async getAuditTrail(params = {}) {
    try {
      const response = await api.get('/enterprise-logs/audit-trail', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
      if (error.response && error.response.status === 401) {
        return {
          status: 0,
          message: 'Authentication required to access audit trail',
          data: null
        };
      }
      throw error;
    }
  }

  /**
   * Get security event logs
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} API response with security events data
   */
  static async getSecurityEvents(params = {}) {
    try {
      const response = await api.get('/enterprise-logs/security-events', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch security events:', error);
      if (error.response && error.response.status === 401) {
        return {
          status: 0,
          message: 'Authentication required to access security events',
          data: null
        };
      }
      throw error;
    }
  }

  /**
   * Get performance metrics logs
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} API response with performance metrics data
   */
  static async getPerformanceMetrics(params = {}) {
    try {
      const response = await api.get('/enterprise-logs/performance-metrics', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch performance metrics:', error);
      if (error.response && error.response.status === 401) {
        return {
          status: 0,
          message: 'Authentication required to access performance metrics',
          data: null
        };
      }
      throw error;
    }
  }

  /**
   * Get comprehensive statistics and analytics
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} API response with statistics
   */
  static async getStatistics(params = {}) {
    try {
      const response = await api.get('/enterprise-logs/statistics', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch enterprise log statistics:', error);
      if (error.response && error.response.status === 401) {
        return {
          status: 0,
          message: 'Authentication required to access log statistics',
          data: null
        };
      }
      throw error;
    }
  }

  /**
   * Get available filter options (levels, channels, categories, etc.)
   * @returns {Promise} API response with filter options
   */
  static async getFilterOptions() {
    try {
      const response = await api.get('/enterprise-logs/filter-options');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      if (error.response && error.response.status === 401) {
        return {
          status: 0,
          message: 'Authentication required to access filter options',
          data: {
            levels: [],
            channels: [],
            categories: [],
            components: []
          }
        };
      }
      throw error;
    }
  }

  /**
   * Search logs with advanced text search capabilities
   * @param {string} searchTerm - The search term
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with search results
   */
  static async searchLogs(searchTerm, params = {}) {
    return this.getLogs({ ...params, search: searchTerm });
  }

  /**
   * Get logs filtered by level
   * @param {string} level - The log level (emergency, alert, critical, error, warning, notice, info, debug)
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with filtered logs
   */
  static async getLogsByLevel(level, params = {}) {
    return this.getLogs({ ...params, level });
  }

  /**
   * Get logs filtered by channel
   * @param {string} channel - The channel name
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with filtered logs
   */
  static async getLogsByChannel(channel, params = {}) {
    return this.getLogs({ ...params, channel });
  }

  /**
   * Get logs filtered by category
   * @param {string} category - The category name
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with filtered logs
   */
  static async getLogsByCategory(category, params = {}) {
    return this.getLogs({ ...params, category });
  }

  /**
   * Get logs filtered by component
   * @param {string} component - The component name
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with filtered logs
   */
  static async getLogsByComponent(component, params = {}) {
    return this.getLogs({ ...params, component });
  }

  /**
   * Get logs filtered by user
   * @param {string|number} userId - The user ID
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with filtered logs
   */
  static async getLogsByUser(userId, params = {}) {
    return this.getLogs({ ...params, user_id: userId });
  }

  /**
   * Get logs within a date range
   * @param {string} dateFrom - Start date (ISO format)
   * @param {string} dateTo - End date (ISO format)
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with date-filtered logs
   */
  static async getLogsByDateRange(dateFrom, dateTo, params = {}) {
    return this.getLogs({ 
      ...params, 
      date_from: dateFrom, 
      date_to: dateTo 
    });
  }

  /**
   * Get logs by correlation ID for request tracking
   * @param {string} correlationId - The correlation/request ID
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with correlated logs
   */
  static async getLogsByCorrelation(correlationId, params = {}) {
    return this.getLogs({ ...params, correlation_id: correlationId });
  }

  /**
   * Get recent activity summary
   * @param {number} hours - Number of hours to look back (default: 24)
   * @returns {Promise} API response with recent activity data
   */
  static async getRecentActivity(hours = 24) {
    try {
      const response = await api.get('/enterprise-logs/recent-activity', { 
        params: { hours } 
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
      if (error.response && error.response.status === 401) {
        return {
          status: 0,
          message: 'Authentication required to access recent activity',
          data: null
        };
      }
      throw error;
    }
  }

  /**
   * Export logs to different formats
   * @param {Object} params - Query parameters for filtering
   * @param {string} format - Export format ('csv', 'json', 'xml')
   * @returns {Promise} API response with exported data
   */
  static async exportLogs(params = {}, format = 'csv') {
    try {
      const response = await api.get('/enterprise-logs/export', { 
        params: { ...params, format },
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Failed to export logs:', error);
      throw error;
    }
  }

  // ========== LOG LEVEL UTILITIES ==========

  /**
   * Get log level color for UI display
   * @param {string} level - Log level
   * @returns {string} CSS class for color styling
   */
  static getLevelColor(level) {
    const colors = {
      emergency: 'bg-red-900 text-white',
      alert: 'bg-red-700 text-white',
      critical: 'bg-red-600 text-white',
      error: 'bg-red-500 text-white',
      warning: 'bg-yellow-500 text-white',
      notice: 'bg-blue-500 text-white',
      info: 'bg-blue-400 text-white',
      debug: 'bg-gray-500 text-white'
    };
    return colors[level?.toLowerCase()] || colors.info;
  }

  /**
   * Get log level priority for sorting
   * @param {string} level - Log level
   * @returns {number} Priority number (higher = more severe)
   */
  static getLevelPriority(level) {
    const priorities = {
      emergency: 8,
      alert: 7,
      critical: 6,
      error: 5,
      warning: 4,
      notice: 3,
      info: 2,
      debug: 1
    };
    return priorities[level?.toLowerCase()] || 2;
  }

  /**
   * Format log context data for display
   * @param {Object|string} context - Log context data
   * @returns {Object} Formatted context object
   */
  static formatContext(context) {
    if (!context) return {};
    
    try {
      return typeof context === 'string' ? JSON.parse(context) : context;
    } catch (error) {
      console.error('Failed to parse log context:', error);
      return {};
    }
  }
}

export default EnterpriseLogService;